import { Component, OnInit } from '@angular/core';
import { DataTaskService } from '../../../core/services/data-task.service';
import { CustomerItem, TypeaheadService } from '../../../services/typeahead.service';
import { Item } from '../../../core/components/listbox/listbox.component';
import { GridOptions } from '../../../core/components/grid/grid.types';
import { Grid } from '../../../core/components/grid/grid.model';

@Component({
    selector: 'tt-forecast-data-grid',
    templateUrl: './forecast-data-grid.component.html',
    styleUrls: ['./forecast-data-grid.component.css'],
    standalone: false
})
export class ForecastDataGridComponent implements OnInit {
    public page?: ForecastDataGridGet;

    public selectListForecasts: Item[] = [];

    public selectListSeasonalities: Item[] = [];

    public selectListResolutions: ResolutionItem[] = [];

    public grid?: GridOptions;

    public executing = false;

    constructor(private datatask: DataTaskService, private typeahead: TypeaheadService) { }

    private async loadPage() {
        this.page = (await this.datatask.Post(3448))[0];
    }

    private async loadForecasts() {
        this.selectListForecasts = await this.datatask.Post(855);
    }

    private async loadSeasonalities() {
        this.selectListSeasonalities = await this.datatask.Post(853);
    }

    private async loadResolutions() {
        this.selectListResolutions = await this.datatask.Post(857);
    }

    private async loadGrid(): Promise<void> {
        if (!this.grid) {
            this.grid = new Grid()
                .setLoadDataFunction({
                    method: 3446,
                    parameters: () => this.page,
                    dynamic: true
                })
                .setKendoConfig({ height: '100%' })
                .setToolbar({ wrapping: true });
        } else {
            await this.grid.gridfunc?.read();
        }
    }

    public async executeGrid(): Promise<void> {
        this.executing = true;

        try {
            await this.loadGrid();
        } finally {
            this.executing = false;
        }
    }

    public searchSuppliers = () => {
        return this.typeahead.searchCustomer({
            cust_name: this.page!.searchtext_supplier,
            custtype: 'S',
            lastselect: 'SEARCH'
        });
    }

    public onSupplierSelected(event?: CustomerItem) {
        if (!this.page) return;

        this.page.supplier_no = !!event ? '' + event.item_id : '0';
        this.page.searchtext_supplier = event?.item_name ?? '';
    }

    private async retrieveHistoryPercentage() {
        if (!this.page) return;

        const response = (await this.datatask.Post(3447, this.page))[0];

        this.page.history_pst = response?.history_pst;
    }

    private async retrieveForecast() {
        if (!!this.page?.forecast_keyno) {
            const response = (await this.datatask.Post(856, { forecast_keyno: this.page.forecast_keyno }))[0];

            this.page.forecast_type_id = response?.forecast_type_id;
            this.page.forecast_resolution_id = response?.forecast_resolution_id;
            this.page.forecast_forward_nbr = response?.forecast_forward_nbr;
            this.page.forecast_history_running_nbr = response?.forecast_history_running_nbr;

            if (response?.forecast_type_id === 'F') {
                this.page.history_fom_date = response?.forecast_history_fixed_fom_date;
                this.page.history_tom_date = response?.forecast_history_fixed_tom_date;
                this.page.forecast_forward_fixed_startdate = response?.forecast_forward_fixed_startdate;
            }
        }
    }

    private async retrieveHistoryDates() {
        if (!this.page) return;

        const response = (await this.datatask.Post(862, {
            forecast_resolution_id: this.page.forecast_resolution_id,
            forecast_history_running_nbr: this.page.forecast_history_running_nbr
        }))[0];

        this.page.history_fom_date = response?.history_fom_date;
        this.page.history_tom_date = response?.history_tom_date;
    }

    public onChanged(_: string, id: keyof ForecastDataGridGet) {
        if (!this.page) return;

        switch (id) {
            case 'history_fom_date':
            case 'history_tom_date':
            case 'seasonality_keyno':
                this.retrieveHistoryPercentage();
                break;
            case 'forecast_keyno':
                this.retrieveForecast();
                break;
            case 'forecast_history_running_nbr':
            case 'forecast_resolution_id':
                if (this.page.forecast_type_id === 'R') this.retrieveHistoryDates();
                break;
            default:
                break;
        }
    }

    private async init() {
        Promise.all([
            this.loadPage(),
            this.loadForecasts(),
            this.loadSeasonalities(),
            this.loadResolutions()
        ]).then(() => {
            this.loadGrid();
            Promise.resolve();
        });
    }

    ngOnInit() {
        this.init();
    }
}

interface ForecastDataGridGet {
    show_supplier_no: string;
    supplier_no: string;
    searchtext_supplier: string;
    seasonality_keyno: string;
    forecast_keyno: string;
    history_fom_date: string;
    history_tom_date: string;
    forecast_type_id: string;
    forecast_resolution_id: string;
    forecast_forward_nbr: string;
    forecast_history_running_nbr: string;
    forecast_forward_text: string;
    forecast_history_text: string;
    history_pst: string;
    forecast_forward_fixed_startdate: string;
}

interface ResolutionItem {
    forecast_resolution_id: string;
    color_name: string;
    orderby: number;
}
